import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getPerformance } from 'firebase/performance';

const firebaseConfig = {
    apiKey: "AIzaSyBt3gvsCCaKJaMJ9JAhrIeqipv76d_q9AI",
    authDomain: "martialartnetwork.com.au",
    projectId: "martial-arts-system",
    storageBucket: "martial-arts-system.firebasestorage.app",
    messagingSenderId: "519285225118",
    appId: "1:519285225118:web:aded22be485862d8f4ad8f",
    measurementId: "G-DXG6SBY4XH"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check (reCAPTCHA Enterprise)
if (typeof window !== 'undefined') {
    initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider('6LepSVksAAAAAEQGWt22EFtGp-MU-ba6o4yb87na'),
        isTokenAutoRefreshEnabled: true
    });
}

const isBrowser = typeof window !== 'undefined';

// Auth. Deliberately does NOT call setPersistence — do not add it back.
//
// getAuth() already negotiates [indexedDBLocalPersistence, browserLocalPersistence,
// browserSessionPersistence], filters that list by real availability, and migrates
// any existing session into the selected store (write-before-delete, same storage
// key across stores, so nobody is signed out by the hop).
//
// Forcing browserLocalPersistence bypassed that negotiation entirely:
// PersistenceUserManager.setPersistence never calls _isAvailable(), and it is
// delete-before-write — it removed the session from the working store and then
// threw writing to localStorage wherever localStorage is blocked (Firefox with
// dom.storage.enabled=false, ITP-partitioned webviews). The tab stayed signed in
// from memory and the user was silently signed out on their next load. The call
// was also unawaited and uncaught, so that throw was an unhandled rejection.
// Removed 2026-08-01 (JAVASCRIPT-REACT-B follow-up).
export const auth = getAuth(app);

// Initialize Firestore — disable browser-only options during SSG
export const db = initializeFirestore(app, {
    experimentalForceLongPolling: isBrowser,
    ...(isBrowser ? { localCache: { kind: 'persistent' as const } } : {})
}, 'martialartsnetwork');

export const locationsDb = initializeFirestore(app, {
    experimentalForceLongPolling: isBrowser,
    ...(isBrowser ? { localCache: { kind: 'persistent' as const } } : {})
}, 'locations');

// Initialize Functions
export const functions = getFunctions(app, 'australia-southeast1');

// Initialize Performance Monitoring — production only, never in local dev
if (!import.meta.env.DEV && isBrowser) {
    try {
        getPerformance(app);
    } catch (e) {
        console.warn('Firebase Performance Monitoring failed to init:', e);
    }
}

// Connect to emulators in development
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true' || (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS !== 'false');

if (useEmulators && isBrowser) {
    // Only connect if not already connected
    const isEmulatorConnected = (window as any).__FIREBASE_EMULATOR_CONNECTED__;

    if (!isEmulatorConnected) {
        try {
            connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
            connectFirestoreEmulator(db, '127.0.0.1', 8080);
            connectFirestoreEmulator(locationsDb, '127.0.0.1', 8080);
            connectFunctionsEmulator(functions, 'localhost', 5001);
            (window as any).__FIREBASE_EMULATOR_CONNECTED__ = true;
            // console.log('🔥 Connected to Firebase Emulators (Auth, Firestore, Functions)');
        } catch (error) {
            // Emulators may already be connected or not running
            // console.log('Firebase Emulators not connected:', error);
        }
    }
}

export default app;
